<template>
  <div class="book-bar" :class="'book-bar--' + info.key">
    <div class="book-bar__controls">
      <div class="book-bar__field" v-if="businesses.length > 1">
        <label class="book-bar__label" :for="uid + '_business'">Empresa</label>
        <b-form-select :id="uid + '_business'" size="sm" class="book-bar__business" :value="book.business_id" :options="businesses" @change="select($event, book.declared)" />
      </div>
      <div class="book-bar__field">
        <span class="book-bar__label" :id="uid + '_book'">Libro</span>
        <div class="book-bar__segmented" role="radiogroup" :aria-labelledby="uid + '_book'">
          <button v-for="option in options"
                  :key="option.key"
                  type="button"
                  role="radio"
                  class="book-bar__option"
                  :class="['book-bar__option--' + option.key, { 'book-bar__option--active': option.key == info.key }]"
                  :aria-checked="option.key == info.key ? 'true' : 'false'"
                  @click="select(book.business_id, option.value)">
            {{ option.label }}
          </button>
        </div>
      </div>
    </div>
    <div class="book-bar__context">
      <span class="book-bar__stripe" aria-hidden="true"></span>
      <span>
        {{ verb }} <b>{{ businessName }}</b> · <span class="book-bar__tag">{{ info.title }}</span>
        <small class="book-bar__note" v-if="note">{{ note }}</small>
        <small class="book-bar__note" v-if="$slots.default"><slot /></small>
      </span>
    </div>
  </div>
</template>
<script>
  import AccountingBook, { bookInfo } from './accountingBook'

  // Selector único de empresa y libro de las pantallas contables. Emite change al quedar listo y en cada cambio.
  export default {
    props: {
      businesses: {
        type: Array,
        default: () => [],
      },
      allowBoth: {
        type: Boolean,
        default: true,
      },
      verb: {
        type: String,
        default: 'Estás viendo',
      },
      note: {
        type: String,
        default: '',
      },
    },
    data() {
      return {
        uid: 'book_' + Math.random().toString(36).slice(2, 8),
        emitted: null,
      }
    },
    computed: {
      book() {
        return AccountingBook.state
      },
      info() {
        return bookInfo(this.book.declared)
      },
      options() {
        const options = [
          { key: 'd', value: true, label: 'Declarado' },
          { key: 'nd', value: false, label: 'No declarado' },
        ]
        if(this.allowBoth) options.push({ key: 'both', value: null, label: 'Ambos' })
        return options
      },
      businessName() {
        const business = this.businesses.find(item => item.value == this.book.business_id)
        return business ? business.text : 'Empresa'
      },
    },
    watch: {
      businesses() {
        this.sync()
      },
    },
    mounted() {
      this.sync()
    },
    // Con keep-alive: otra pantalla pudo cambiar el libro mientras esta estaba oculta
    activated() {
      this.sync()
    },
    methods: {
      select(businessId, declared) {
        AccountingBook.set(businessId, declared)
        this.sync()
      },
      // Corrige lo guardado contra lo que admite la pantalla y avisa solo si cambió
      sync() {
        if(!this.businesses.length) return

        let businessId = this.book.business_id
        if(!this.businesses.some(item => item.value == businessId)) {
          businessId = this.businesses[0].value
        }
        let declared = this.book.declared
        if(declared === null && !this.allowBoth) {
          declared = true
        }
        if(businessId !== this.book.business_id || declared !== this.book.declared) {
          AccountingBook.set(businessId, declared)
        }

        const key = businessId + '_' + declared
        if(this.emitted === key) return
        this.emitted = key
        this.$emit('change', { business_id: businessId, declared: declared })
      },
    },
  }
</script>
<style scoped>
  .book-bar {
    --book-color: var(--app-color-border-strong);
    --book-soft: var(--app-color-surface-subtle);
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    justify-content: space-between;
    gap: 10px 16px;
    margin-top: 8px;
    padding: 8px 12px;
    background: var(--book-soft);
    border: 1px solid var(--app-color-border);
    border-left: 4px solid var(--book-color);
    border-radius: 8px;
  }
  .book-bar--d {
    --book-color: var(--app-color-info);
    --book-soft: var(--app-color-info-soft);
  }
  .book-bar--nd {
    --book-color: var(--app-color-warning);
    --book-soft: var(--app-color-warning-soft);
  }
  .book-bar__controls {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 12px;
  }
  .book-bar__field {
    display: flex;
    flex-direction: column;
  }
  .book-bar__label {
    font-size: 11px;
    font-weight: 600;
    margin-bottom: 2px;
    color: var(--app-color-text-secondary);
  }
  .book-bar__business {
    min-width: 180px;
  }
  .book-bar__segmented {
    display: inline-flex;
    border: 1px solid var(--app-color-border-strong);
    border-radius: 6px;
    overflow: hidden;
    background: var(--app-color-surface);
  }
  .book-bar__option {
    padding: 4px 12px;
    font-size: 13px;
    font-weight: 600;
    color: var(--app-color-text-secondary);
    background: transparent;
    border: 0;
    border-right: 1px solid var(--app-color-border);
    cursor: pointer;
  }
  .book-bar__option:last-child {
    border-right: 0;
  }
  .book-bar__option:hover {
    background: var(--app-color-surface-subtle);
  }
  .book-bar__option:focus-visible {
    outline: 2px solid var(--app-color-primary);
    outline-offset: -2px;
  }
  .book-bar__option--active.book-bar__option--d {
    color: var(--app-color-white);
    background: var(--app-color-info);
  }
  .book-bar__option--active.book-bar__option--nd {
    color: var(--app-color-text);
    background: var(--app-color-warning);
  }
  .book-bar__option--active.book-bar__option--both {
    color: var(--app-color-white);
    background: var(--app-color-text-secondary);
  }
  .book-bar__context {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
  }
  .book-bar__stripe {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: var(--book-color);
    flex-shrink: 0;
  }
  .book-bar__tag {
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }
  .book-bar__note {
    display: block;
    color: var(--app-color-text-secondary);
  }
</style>
